import { Close, Content, Description, Overlay, Portal, Root, Title } from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { cn } from '@avanta/shared/utils/cn';
import { Button } from '../Button';
import { type DialogProps } from './types';

const DEFAULT_TITLE = 'Dialog.title';
const DEFAULT_DESCRIPTION = 'Dialog.description';

export const Dialog = ({
  className,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  open = false,
  hideClose = false,
  onClose,
  enableOutsideClick = true,
  ...props
}: React.PropsWithChildren<DialogProps>): React.ReactElement => {
  const { isOpen, hideDialog } = useDialogContext();
  const { t } = useTranslation(LOCIZE_NS.COMMON);

  const handleClose = (): void => {
    onClose?.();
    hideDialog();
  };

  const handlePrimaryAction = (event: React.MouseEvent<HTMLButtonElement>): void => {
    primaryAction?.action?.(event);
    handleClose();
  };

  const handleSecondaryAction = (event: React.MouseEvent<HTMLButtonElement>): void => {
    secondaryAction?.action?.(event);
    handleClose();
  };

  return (
    <Root open={open ?? isOpen} data-slot="dialog" {...props}>
      <Portal data-slot="dialog-portal">
        <Overlay
          data-slot="dialog-overlay"
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50"
          onClick={enableOutsideClick ? handleClose : undefined}
        />
        <Content
          data-slot="dialog-content"
          className={cn(
            'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed am-lg:top-[35%] top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] am-lg:max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-8 rounded-3xl pt-6 px-8 pb-8 border shadow-lg duration-200 notranslate',
            className
          )}
          translate="no"
        >
          <VisuallyHidden>
            {!title && <Title>{t(DEFAULT_TITLE)}</Title>}
            {!description && <Description>{t(DEFAULT_DESCRIPTION)}</Description>}
          </VisuallyHidden>
          {(title || description) && (
            <div data-slot="dialog-header" className="flex flex-col gap-2 text-left">
              <Title data-slot="dialog-title" className="text-xl font-medium" {...props}>
                {title}
              </Title>
              <Description
                data-slot="dialog-description"
                className="text-gray-600 text-sm"
                {...props}
              >
                {description}
              </Description>
            </div>
          )}
          {children}
          {(primaryAction || secondaryAction) && (
            <div
              data-slot="dialog-footer"
              className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"
            >
              {secondaryAction && (
                <Button
                  variant={secondaryAction.variant || 'terciary'}
                  onClick={handleSecondaryAction ?? handleClose}
                  disabled={secondaryAction.disabled}
                  className={secondaryAction.className}
                  isLoading={secondaryAction.isLoading}
                  size={secondaryAction.size || 'md'}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {primaryAction && (
                <Button
                  variant={primaryAction.variant || 'primary'}
                  onClick={handlePrimaryAction ?? handleClose}
                  disabled={primaryAction.disabled}
                  className={primaryAction.className}
                  isLoading={primaryAction.isLoading}
                  size={primaryAction.size || 'md'}
                >
                  {primaryAction.label}
                </Button>
              )}
            </div>
          )}
          {!hideClose && (
            <Close
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-mint-300 data-[state=open]:text-gray-600 absolute top-3 right-3 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 dialog-close-button"
              onClick={handleClose}
            >
              <XIcon className="size-6 text-denim-500 hover:text-denim-600" strokeWidth={1.25} />
              <span className="sr-only">{t('Dialog.close')}</span>
            </Close>
          )}
        </Content>
      </Portal>
    </Root>
  );
};
